import React, { useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

function useCenteredOffsets(rootRef, targets, deps = []) {
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return undefined;

        let frameId = 0;
        const targetItems = targets.filter(({ ref }) => ref.current);

        const align = () => {
            const rootEl = rootRef.current;
            if (!rootEl) return;

            const rootRect = rootEl.getBoundingClientRect();
            const centerY = rootRect.top + rootRect.height / 2;

            targetItems.forEach(({ ref, varName }) => {
                const element = ref.current;
                if (!element) return;

                const rect = element.getBoundingClientRect();
                const offset = centerY - (rect.top + rect.height / 2);
                rootEl.style.setProperty(varName, `${offset}px`);
            });
        };

        const scheduleAlign = () => {
            cancelAnimationFrame(frameId);
            frameId = window.requestAnimationFrame(align);
        };

        scheduleAlign();

        const observers = [];
        if (typeof ResizeObserver !== 'undefined') {
            [rootRef, ...targetItems.map(({ ref }) => ref)].forEach((ref) => {
                const element = ref.current;
                if (!element) return;

                const observer = new ResizeObserver(scheduleAlign);
                observer.observe(element);
                observers.push(observer);
            });
        }

        const viewport = window.visualViewport || null;
        window.addEventListener('resize', scheduleAlign);
        viewport?.addEventListener('resize', scheduleAlign);

        if (document.fonts?.ready) {
            document.fonts.ready.then(scheduleAlign).catch(() => { });
        }

        return () => {
            cancelAnimationFrame(frameId);
            observers.forEach((observer) => observer.disconnect());
            window.removeEventListener('resize', scheduleAlign);
            viewport?.removeEventListener('resize', scheduleAlign);
        };
    }, deps);
}

function CalendarIcon({ className = '' }) {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={className}
        >
            <path
                d="M7.5 2.75v2.5M16.5 2.75v2.5M3.75 8.75h16.5M6 4.75h12A2.25 2.25 0 0 1 20.25 7v11A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V7A2.25 2.25 0 0 1 6 4.75Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function TagIcon({ className = '' }) {
    return (
        <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={className}
        >
            <path
                d="M10.25 4.75H6.75A2.25 2.25 0 0 0 4.5 7v4.043a2.25 2.25 0 0 0 .659 1.591l5.707 5.707a2.25 2.25 0 0 0 3.182 0l4.293-4.293a2.25 2.25 0 0 0 0-3.182l-5.909-5.909a2.25 2.25 0 0 0-1.591-.659Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle cx="8.25" cy="8.25" r="1.1" fill="currentColor" />
        </svg>
    );
}

function MetaPill({ label, Icon, className = '' }) {
    return (
        <span
            className={`inline-flex h-6 flex-nowrap items-center gap-1 rounded-full border border-white/15 bg-white/10 px-2.5 text-slate-300 shadow-[0_2px_10px_rgba(15,23,42,0.10)] transition hover:bg-white/14 hover:shadow-[0_4px_14px_rgba(15,23,42,0.14)] ${className}`}
            style={{
                lineHeight: 1,
            }}
        >
            <Icon className="block h-3 w-3 shrink-0" />
            <span className="whitespace-nowrap font-sans text-xs leading-none" style={{ lineHeight: 1 }}>
                {label}
            </span>
        </span>
    );
}

function ActionButton({ children, to, href, reloadDocument = false, className = '' }) {
    const sharedClassName = `relative overflow-hidden rounded-full border border-white/50 bg-white/15 px-4 font-semibold text-white no-underline shadow-[0_2px_10px_rgba(15,23,42,0.14)] transition hover:bg-white/22 hover:shadow-[0_4px_14px_rgba(15,23,42,0.2)] ${className}`;
    const ButtonComponent = to ? Link : 'a';
    const buttonProps = to
        ? { to, reloadDocument }
        : { href };

    return (
        <ButtonComponent
            className={`no-underline ${sharedClassName} flex items-center justify-center `}
            {...buttonProps}
        >
            <div
                className="px-4"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    lineHeight: 1,
                    paddingTop: 0,
                    paddingBottom: 0,
                }}
            >
                <div className="inline-flex items-center whitespace-nowrap leading-none py-2.5">
                    <div
                        className="capsize font-sans text-sm leading-none whitespace-nowrap"
                        style={{
                            display: 'inline-flex',
                            padding: 0,
                            margin: 0,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </ButtonComponent>
    );
}

export default function HomeMetaPills({ dateLabel, categoryLabel, categoryClassName = 'font-semibold' }) {
    return (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <MetaPill
                label={dateLabel}
                Icon={CalendarIcon}
                className="border border-white/15 bg-white/10"
            />
            <MetaPill
                label={categoryLabel}
                Icon={TagIcon}
                className={`border border-white/10 bg-white/5 ${categoryClassName}`}
            />
        </div>
    );
}

export function HomeActionButton(props) {
    return <ActionButton {...props} />;
}
